import React, { FC, useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Modal, message, Popconfirm, Button, Select, Input, Checkbox, Switch, Dropdown, Space } from "antd";
import type { CheckboxProps, MenuProps } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import JSZip from 'jszip';
import { getTaskListApi } from "../../service/folders";
import { delTaskApi, batchDelTaskApi, putNameTaskApi, submitResourceApi, updateVideoTagApi, retryTaskApi, submitForReviewApi, revokeSubmissionApi, approveTaskApi } from "../../service/myWork";
import { getSeriesSpeakersApi } from "../../service/speakers";
import { getGroupMembersByGroupNoApi } from "../../service/profile";
import { getLanguageName, getAllLanguages } from "../../utils/languageUtils";
import { AlertMessage, BackButton, VideoCardSkeleton, VideoPlayerModal, EpisodeDrawer } from "../../components/common";
import { useAlert } from "../../hooks/useAlert";
import { ALERT_Z_INDEX } from "../../utils/alertManager";
import { getVideoPlayUrl } from "../../utils/videoUrl";
import "../../styles/reset.scss";
import "./VideoList.scss";
import { getSeriesListApi, getSeriesDetailApi, addSeriesLanguagesBatchApi } from "../../service/series";
import { getPureVideoListApi, getMultilingualVideoListApi, getMissingTasksApi, importTasksApi, toTerminologyJson } from "../../service/task";
import { VIDEO_CDN_CONFIG, VIDEO_STORAGE_CONFIG, getEnv, getVideoStoragePrefix } from "../../config/video";
import { PermissionGuard } from "../../components/common/PermissionGuard";
import { useUserRole } from "../../hooks/useUserRole";
import { globalStore } from "../../utils/globalStore";
import { useVideoTagPermission } from "../../utils/videoTagPermission";
import { COPYRIGHT_TEXT } from "../../config/constants";
import { Index403 } from "../403/Index403";
import { UserRoleManager } from "../../utils/userRole";

// 时长格式化函数：秒 → 分:秒（补0处理）
const formatDuration = (seconds: number): string => {
  if (isNaN(seconds)) return '00:00';

  // 计算分、秒
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  // 补0（不足2位补0，如1→01）
  const format = (num: number) => num.toString().padStart(2, '0');
  return `${format(minutes)}:${format(remainingSeconds)}`;
};

// 通过video标签解析时长
const getVideoDurationByUrl = (url: string): Promise<number> => {
  return new Promise((resolve, reject) => {
    // 创建隐藏的video元素
    const video = document.createElement('video');

    // 关键：设置预加载方式为"仅加载元数据"（避免加载视频内容，节省流量）
    video.preload = 'metadata';
    video.muted = true; // 静音避免自动播放
    video.crossOrigin = 'anonymous'; // 处理跨域问题
    video.src = url;

    // 元数据加载成功：获取时长并resolve
    video.addEventListener('loadedmetadata', () => {
      if (video.duration && isFinite(video.duration)) {
        resolve(video.duration); // duration单位：秒
      } else {
        reject(new Error('无法获取视频时长'));
      }
      video.remove(); // 释放资源
    });

    // 加载失败（如URL无效、跨域）：reject错误信息
    video.addEventListener('error', () => {
      const errorMsg = video.error ? video.error.message : '视频加载失败（可能跨域或URL无效）';
      reject(new Error(errorMsg));
      video.remove(); // 释放资源
    });

    // 设置超时处理
    const timeout = setTimeout(() => {
      reject(new Error('获取视频时长超时'));
      video.remove();
    }, 10000); // 10秒超时

    // 清理超时
    video.addEventListener('loadedmetadata', () => clearTimeout(timeout), { once: true });
    video.addEventListener('error', () => clearTimeout(timeout), { once: true });
  });
};

// 视频时长组件
const VideoDuration: FC<{ videoUrl?: string | null }> = ({ videoUrl }) => {
  const [duration, setDuration] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!videoUrl) {
      setDuration(null);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    getVideoDurationByUrl(videoUrl)
      .then((durationSeconds) => {
        setDuration(durationSeconds);
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setIsLoading(false);
      });
  }, [videoUrl]);

  if (isLoading) {
    return <span style={{ opacity: 0.8, fontWeight: '600' }}>--:--</span>;
  }

  if (error || !duration) {
    return <span style={{ opacity: 0.8, fontWeight: '600' }}>00:00</span>;
  }

  return <span style={{ fontWeight: '600' }}>{formatDuration(duration)}</span>;
};

interface Props { }

export const VideoList: FC<Props> = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const seriesId = searchParams.get('seriesId');
  const seriesName = searchParams.get('seriesName') ? decodeURIComponent(searchParams.get('seriesName')!) : null;
  // 语种来自内层 seriesListIn 页点击的语言卡片传入的 langCode
  const urlLangCode = searchParams.get('langCode');
  // 是否显示纯净版视频列表
  const isPure = searchParams.get('isPure') === 'true';
  const { isAdmin, isOperator, isAssessor } = useUserRole(); // 获取用户角色信息（此处保留，后续可用于纯净版内按钮权限）
  const { shouldShowTagButton, getTagButtonActions } = useVideoTagPermission();

  // 从 globalStore 同步获取角色信息，确保首次渲染时就能正确判断
  const [localIsAdmin, setLocalIsAdmin] = useState(() => {
    try {
      const storedUserInfo = globalStore.getUserInfo();
      if (storedUserInfo?.roles) {
        const roleKeys = storedUserInfo.roles.map((role: any) => 
          (typeof role === 'string' ? role : role.role_key || role.code || '').toLowerCase()
        );
        return roleKeys.some((key: string) => 
          key === 'admin' || key === 'super_admin' || key === 'administrator'
        );
      }
    } catch {
      // 忽略错误
    }
    return false;
  });

  const [localIsOperator, setLocalIsOperator] = useState(() => {
    try {
      const storedUserInfo = globalStore.getUserInfo();
      if (storedUserInfo?.roles) {
        const roleKeys = storedUserInfo.roles.map((role: any) => 
          (typeof role === 'string' ? role : role.role_key || role.code || '').toLowerCase()
        );
        return roleKeys.some((key: string) => key === 'operator');
      }
    } catch {
      // 忽略错误
    }
    return false;
  });

  const [localIsAssessor, setLocalIsAssessor] = useState(() => {
    try {
      const storedUserInfo = globalStore.getUserInfo();
      if (storedUserInfo?.roles) {
        const roleKeys = storedUserInfo.roles.map((role: any) => 
          (typeof role === 'string' ? role : role.role_key || role.code || '').toLowerCase()
        );
        return roleKeys.some((key: string) => key === 'assessor' || key === 'reviewer');
      }
    } catch {
      // 忽略错误
    }
    return false;
  });

  // 同步 useUserRole 的角色信息到本地状态
  useEffect(() => {
    if (isAdmin) {
      setLocalIsAdmin(true);
    }
    if (isOperator) {
      setLocalIsOperator(true);
    }
    if (isAssessor) {
      setLocalIsAssessor(true);
    }
  }, [isAdmin, isOperator, isAssessor]);

  // 监听 globalStore 变化，确保角色信息更新时能及时同步
  useEffect(() => {
    const unsubscribe = globalStore.subscribe(() => {
      const storedUserInfo = globalStore.getUserInfo();
      if (storedUserInfo?.roles) {
        const roleKeys = storedUserInfo.roles.map((role: any) => 
          (typeof role === 'string' ? role : role.role_key || role.code || '').toLowerCase()
        );
        
        const isAdminRole = roleKeys.some((key: string) => 
          key === 'admin' || key === 'super_admin' || key === 'administrator'
        );
        if (isAdminRole) {
          setLocalIsAdmin(true);
        }
        
        const isOperatorRole = roleKeys.some((key: string) => key === 'operator');
        if (isOperatorRole) {
          setLocalIsOperator(true);
        }
        
        const isAssessorRole = roleKeys.some((key: string) => key === 'assessor' || key === 'reviewer');
        if (isAssessorRole) {
          setLocalIsAssessor(true);
        }
      }
    });
    
    return () => {
      unsubscribe();
    };
  }, []);

  // 判断是否为操作者（优先使用 useUserRole 的结果，如果还未初始化则使用本地状态）
  const isOperatorOnly = useMemo(() => {
    return (isOperator || localIsOperator) && !(isAdmin || localIsAdmin) && !(isAssessor || localIsAssessor);
  }, [isOperator, localIsOperator, isAdmin, localIsAdmin, isAssessor, localIsAssessor]);

  // 判断是否为管理员或审核者（不受限制）
  const isAdminOrAssessor = useMemo(() => {
    return (isAdmin || localIsAdmin) || (isAssessor || localIsAssessor);
  }, [isAdmin, localIsAdmin, isAssessor, localIsAssessor]);

  // 本地缓存权限和菜单（与 Home 页保持一致的方式）
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [menuListState, setMenuListState] = useState<any[]>([]);

  useEffect(() => {
    // 初始化一次
    const user = globalStore.getUserInfo();
    setUserPermissions(user?.permissions || []);
    setMenuListState(globalStore.getMenuList() || []);

    // 订阅全局权限 / 菜单变更
    const unsubscribe = globalStore.subscribe(() => {
      const u = globalStore.getUserInfo();
      setUserPermissions(u?.permissions || []);
      setMenuListState(globalStore.getMenuList() || []);
    });

    return () => {
      unsubscribe && unsubscribe();
    };
  }, []);

  // 检查视频按钮权限的函数（基于本地 state，与 Home 页逻辑一致）
  const checkVideoButtonPermission = useCallback(
    (permission: string): boolean => {
      // 超级管理员 / 管理员：视为拥有所有视频相关按钮权限
      const currentRole = UserRoleManager.getCurrentUserRole();
      if (currentRole?.isAdmin) {
        return true;
      }

      // 先看 permissions 数组
      if (
        userPermissions &&
        userPermissions.length > 0 &&
        userPermissions.includes(permission)
      ) {
        return true;
      }

      // 再看菜单中的 meta.perms
      const menuList = menuListState;
      if (!menuList || menuList.length === 0) {
        return false;
      }

      const findMenuPermission = (menus: any[], targetPermission: string): boolean => {
        for (const menu of menus) {
          if (menu.meta?.perms === targetPermission) {
            return true;
          }
          if (menu.children && menu.children.length > 0) {
            if (findMenuPermission(menu.children, targetPermission)) {
              return true;
            }
          }
        }
        return false;
      };

      return findMenuPermission(menuList, permission);
    },
    [userPermissions, menuListState]
  );

  // 检查视频播放权限
  const hasVideoPlayPermission = (): boolean => {
    return checkVideoButtonPermission('video:multilingual:play');
  };

  // 检查视频删除权限
  const hasVideoDeletePermission = (): boolean => {
    return checkVideoButtonPermission('video:clean:delete');
  };

  // 检查视频刷新权限
  const hasVideoRefreshPermission = (): boolean => {
    return checkVideoButtonPermission('video:multilingual:refresh');
  };

  // 批量下载权限（右上角"下载"按钮）
  const hasBatchDownloadPermission = (): boolean => {
    return checkVideoButtonPermission('video:multilingual:batch:download');
  };

  // 批量删除权限（右上角"删除"按钮 & 单个视频删除）
  const hasBatchDeletePermission = (): boolean => {
    return checkVideoButtonPermission('video:clean:delete');
  };

  // 检查视频tag权限
  const hasVideoTagPermission = (): boolean => {
    return checkVideoButtonPermission('videoBtn:tag');
  };

  // 检查上传视频权限（纯净版视频列表使用）
  const hasUploadVideoPermission = (): boolean => {
    return checkVideoButtonPermission('video:clean:upload');
  };

  // 检查一键导入权限（普通视频列表使用）
  const hasImportVideoPermission = (): boolean => {
    return checkVideoButtonPermission('video:multilingual:import');
  };

  // 删除分页相关状态
  // const [currentPage, setCurrentPage] = useState(1);
  // const [pageSize, setPageSize] = useState(12);
  const [sortBy, setSortBy] = useState<'time' | 'alphabet'>('alphabet');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInputValue, setSearchInputValue] = useState(''); // 用于输入框显示，立即更新
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null); // 防抖定时器
  const { alertConfig, hideAlert, showSuccess, showError, showInfo, showWarning } = useAlert();
  const { TextArea } = Input;

  // 新增语种弹窗相关状态
  const [addLanguageModalVisible, setAddLanguageModalVisible] = useState(false);
  const [selectedNewLangCodes, setSelectedNewLangCodes] = useState<string[]>([]);
  const [aiVoiceEnabled, setAiVoiceEnabled] = useState(false);
  const [styleConfigs, setStyleConfigs] = useState<
    { sourceLang: string; text: string }[]
  >([
    { sourceLang: 'en', text: '' },
  ]);

  // 从语言工具中获取所有支持的语言，而不是在这里写死
  const allLanguagesRecord = getAllLanguages();
  const availableLanguages = Object.entries(allLanguagesRecord).map(
    ([code, label]) => ({ code, label })
  );

  const toggleNewLanguage = (code: string) => {
    setSelectedNewLangCodes((prev) => {
      // 如果当前已选中该语种，则取消选中
      if (prev.includes(code)) {
        return prev.filter((c) => c !== code);
      }
      // 如果已选中的语种数量达到5个，则提示并不允许继续选择
      if (prev.length >= 5) {
        showWarning('最多只能选择5个翻译语种');
        return prev;
      }
      // 否则添加该语种
      return [...prev, code];
    });
  };

  const handleAddStyleConfigRow = () => {
    setStyleConfigs((prev) => [...prev, { sourceLang: 'en', text: '' }]);
  };

  const handleStyleConfigChange = (
    index: number,
    field: 'sourceLang' | 'text',
    value: string,
  ) => {
    setStyleConfigs((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  const handleConfirmAddLanguage = async () => {
    if (!seriesId) {
      showError('缺少剧集ID，无法新增语种');
      return;
    }

    // 检查是否选中了视频
    if (!selectedVideoIds || selectedVideoIds.length === 0) {
      showWarning('您未选中视频，请选中后操作');
      return;
    }

    if (!selectedNewLangCodes.length) {
      showWarning('请先选择要新增的语种');
      return;
    }

    try {
      showInfo('正在添加语种，请稍候...');
      
      // 处理翻译术语设置：将用户输入的原始文本转义为 JSON 格式
      const transTerms: Array<{ langCode: string; terminology: string }> = [];
      if (styleConfigs && styleConfigs.length > 0) {
        styleConfigs.forEach(config => {
          if (config.text && config.text.trim()) {
            // 使用 toTerminologyJson 转义术语文本
            const escapedTerminology = toTerminologyJson(config.text);
            transTerms.push({
              langCode: config.sourceLang,
              terminology: escapedTerminology
            });
          }
        });
      }
      
      // 构建请求参数
      const requestBody: {
        seriesId: number;
        langCodes: string[];
        taskIds: number[];
        transTerms?: Array<{ langCode: string; terminology: string }>;
        aiVoiceover?: boolean;
      } = {
        seriesId: parseInt(seriesId, 10),
        langCodes: selectedNewLangCodes,
        taskIds: selectedVideoIds,
      };
      
      // 如果有翻译术语，添加到请求参数中
      if (transTerms.length > 0) {
        requestBody.transTerms = transTerms;
      }
      
      // 如果启用了 AI 配音，添加到请求参数中
      if (aiVoiceEnabled) {
        requestBody.aiVoiceover = true;
      }
      
      const result = await addSeriesLanguagesBatchApi(requestBody);

      if (result && result.code === 0) {
        showSuccess('新增语种成功');
        setAddLanguageModalVisible(false);
        setSelectedNewLangCodes([]);
        setStyleConfigs([{ sourceLang: 'en', text: '' }]);
        setAiVoiceEnabled(false);
        // 清空选中的视频ID列表
        setSelectedVideoIds([]);
        // 隐藏批量操作按钮
        setShowBatchActions(false);
        await refresh();
        // 新增语种成功后刷新头部积分
        window.dispatchEvent(new Event('points:refresh'));
        // 跳转到内层剧集页
        if (seriesId) {
          navigate(
            `/seriesListIn?seriesId=${seriesId}&seriesName=${encodeURIComponent(
              seriesName || ""
            )}`
          );
        }
      } else {
        showError(result?.msg || '新增语种失败，请稍后重试');
      }
    } catch (error: any) {
      console.error('新增语种失败:', error);
      let messageText = '新增语种失败，请稍后重试';
      if (error?.response?.data?.msg) {
        messageText = error.response.data.msg;
      } else if (error?.message) {
        messageText = error.message;
      }
      showError(messageText);
    }
  };

  // 检查localStorage中的提示信息
  useEffect(() => {
    const checkVideoGenerateAlert = () => {
      try {
        const alertData = localStorage.getItem('videoGenerateAlert');
        if (alertData) {
          const { type, message, timestamp } = JSON.parse(alertData);

          // 检查时间戳，如果超过5分钟则不显示
          const now = Date.now();
          if (now - timestamp < 5 * 60 * 1000) {
            // 显示提示信息
            if (type === 'success') {
              showSuccess(message);
            } else if (type === 'error') {
              showError(message);
            } else if (type === 'warning') {
              showInfo(message); // 使用showInfo代替showWarning
            } else {
              showInfo(message);
            }
          }

          // 清除localStorage中的提示信息
          localStorage.removeItem('videoGenerateAlert');
        }
      } catch (error) {
        console.error('解析localStorage中的提示信息失败:', error);
        // 清除可能损坏的数据
        localStorage.removeItem('videoGenerateAlert');
      }
    };

    checkVideoGenerateAlert();
  }, [showSuccess, showError, showInfo]);

  const [apiData, setApiData] = useState<Task.GetTaskListResult>({
    data: [],
    pagination: {
      page: 1,
      per_page: 10,
      total: 0,
      pages: 0,
      has_next: false,
      has_prev: false,
      sort: "alphabet"
    }
  });
  const [isLoading, setIsLoading] = useState(true);

  // 双击编辑相关状态
  const [editingItems, setEditingItems] = useState<Set<number>>(new Set());
  const [editingNames, setEditingNames] = useState<Record<number, string>>({});

  // 剧集信息状态（用于抽屉组件）
  const [currentEpisodeInfo, setCurrentEpisodeInfo] = useState({
    name: '',
    sourceLanguage: '',
    targetLanguage: '',
    needRemovalSubtitle: false
  });
  // 剧集信息加载状态
  const [isLoadingSeriesInfo, setIsLoadingSeriesInfo] = useState(true);

  // 说话人数据状态
  const [speakersData, setSpeakersData] = useState<Speakers.Speaker[]>([]);
  const [speakersLoading, setSpeakersLoading] = useState(false);
  const [characterProfiles, setCharacterProfiles] = useState<Series.CharacterProfile[]>([]);

  // 批量操作按钮显示状态
  const [showBatchActions, setShowBatchActions] = useState(false);
  // 选中视频 ID 列表
  const [selectedVideoIds, setSelectedVideoIds] = useState<number[]>([]);

  // 视频播放弹窗状态
  const [videoModalVisible, setVideoModalVisible] = useState(false);

  // 一键导入弹窗相关状态
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [missingTasks, setMissingTasks] = useState<Task.PureVideoListItem[]>([]);
  const [loadingMissingTasks, setLoadingMissingTasks] = useState(false);
  const [currentVideoData, setCurrentVideoData] = useState<{
    url: string;
    name: string;
    status: string;
    createTime: string;
    message: string;
    id: string;
  } | null>(null);

  // 底部操作按钮状态
  const [actionMenu, setActionMenu] = useState<{
    visible: boolean;
    videoId: number | null;
  }>({
    visible: false,
    videoId: null
  });

  // 监听全局点击事件，关闭操作菜单
  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // 如果点击的不是操作按钮或操作菜单，则关闭菜单
      if (!target.closest('.action-btn.pin-btn') && !target.closest('.action-bubble-menu')) {
        closeActionMenu();
      }
    };

    if (actionMenu.visible) {
      document.addEventListener('click', handleGlobalClick);
    }

    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, [actionMenu.visible]);


  const refresh = useCallback(async () => {
    if (!seriesId) return;

    try {
      setIsLoading(true);

      // 如果是纯净版视频列表，调用纯净版接口
      if (isPure) {
        // 将 sortBy 映射到 sort 参数：time -> 1 (按创建时间倒序), alphabet -> 2 (按名称升序)
        const sortValue = sortBy === 'time' ? 1 : 2;
        // 传递搜索关键词（如果有）
        const searchName = searchQuery.trim() || undefined;
        const response = await getPureVideoListApi(parseInt(seriesId), sortValue, searchName);

        if (response.code === 0 && response.data) {
          // 将数据转换为 Task.TaskItem 格式
          // 根据当前环境获取对应的CDN前缀
            // 图片使用 /storage 路径（不使用强缓存）
            const storagePrefix = getVideoStoragePrefix();

          const convertedTasks: Task.TaskItem[] = response.data.map((item: Task.PureVideoListItem) => {
            // 处理封面URL：图片使用 /storage 路径（不使用强缓存）
            const storagePrefix = getVideoStoragePrefix();
            const coverUrl = item.coverUrl
              ? (item.coverUrl.startsWith('http://') || item.coverUrl.startsWith('https://')
                ? item.coverUrl
                : `${storagePrefix}${item.coverUrl.startsWith('/') ? item.coverUrl : `/${item.coverUrl}`}`)
              : null;

            return {
              name: item.name.replace(/\.[^/.]+$/, ''), // 移除文件后缀
              disable_bgm: item.disableBgm ?? false,
              has_user_srt: item.hasUserSrt,
              has_user_script: item.hasUserScript,
              tag: item.tag,
              descript: item.description,
              id: item.id,
              series_id: parseInt(seriesId),
              user_id: 0,
              status: item.status,
              create_time: item.createTime,
              update_time: item.updateTime,
              file_url: item.fileUrl,
              source_hls_url: null,
              result_url: item.resultUrl,
              translated_subtitle_url: null,
              subtitle_url: item.subtitleUrl,
              pure_video_url: item.pureVideoUrl,
              pure_video_hls_url: null,
              cover_url: coverUrl, // 使用处理后的封面URL
              backgroud_audio_url: null,
              script_url: item.scriptUrl,
              duration: item.duration ?? undefined, // 将 null 转换为 undefined
              sourceLanguage: item.langCode,
              targetLanguage: "",
              needRemovalSubtitle: false,
              episodeTasks: [],
            };
          });

          setApiData({
            data: convertedTasks,
            pagination: {
              page: 1,
              per_page: convertedTasks.length,
              total: convertedTasks.length,
              pages: 1,
              has_next: false,
              has_prev: false,
              sort: "alphabet",
            }
          });
          return;
        } else {
          message.error(response.msg || '获取纯净版视频列表失败');
          setApiData({
            data: [],
            pagination: {
              page: 1,
              per_page: 10,
              total: 0,
              pages: 0,
              has_next: false,
              has_prev: false,
              sort: "alphabet"
            }
          });
          return;
        }
      }

      // 如果是多语种版本，调用多语种视频列表接口
      if (urlLangCode) {
        const langCode = urlLangCode;
        // 将 sortBy 映射到 sort 参数：time -> 1 (按创建时间倒序), alphabet -> 2 (按名称升序)
        const sortValue = sortBy === 'time' ? 1 : 2;
        // 传递搜索关键词（如果有）
        const searchName = searchQuery.trim() || undefined;
        const response = await getMultilingualVideoListApi(parseInt(seriesId), langCode, sortValue, searchName);

        if (response.code === 0 && response.data) {
          // 将数据转换为 Task.TaskItem 格式
          // 根据当前环境获取对应的CDN前缀
            // 图片使用 /storage 路径（不使用强缓存）
            const storagePrefix = getVideoStoragePrefix();

          const convertedTasks: Task.TaskItem[] = response.data.map((item: Task.PureVideoListItem) => {
            // 处理封面URL：如果有值则拼接CDN前缀，否则为null
            // 图片使用 /storage 路径（不使用强缓存）
            const storagePrefix = getVideoStoragePrefix();
            const coverUrl = item.coverUrl
              ? (item.coverUrl.startsWith('http://') || item.coverUrl.startsWith('https://')
                ? item.coverUrl
                : `${storagePrefix}${item.coverUrl.startsWith('/') ? item.coverUrl : `/${item.coverUrl}`}`)
              : null;

            return {
              name: item.name.replace(/\.[^/.]+$/, ''), // 移除文件后缀
              disable_bgm: item.disableBgm ?? false,
              has_user_srt: item.hasUserSrt,
              has_user_script: item.hasUserScript,
              tag: item.tag,
              descript: item.description,
              id: item.id,
              series_id: parseInt(seriesId),
              user_id: 0,
              status: item.status,
              create_time: item.createTime,
              update_time: item.updateTime,
              file_url: item.fileUrl,
              source_hls_url: null,
              result_url: item.resultUrl,
              translated_subtitle_url: null,
              subtitle_url: item.subtitleUrl,
              pure_video_url: item.pureVideoUrl,
              pure_video_hls_url: null,
              cover_url: coverUrl, // 使用处理后的封面URL
              backgroud_audio_url: null,
              script_url: item.scriptUrl,
              duration: item.duration ?? undefined, // 将 null 转换为 undefined
              sourceLanguage: item.langCode,
              targetLanguage: "",
              needRemovalSubtitle: false,
              episodeTasks: [],
            };
          });

          setApiData({
            data: convertedTasks,
            pagination: {
              page: 1,
              per_page: convertedTasks.length,
              total: convertedTasks.length,
              pages: 1,
              has_next: false,
              has_prev: false,
              sort: "alphabet",
            }
          });
          return;
        } else {
          message.error(response.msg || '获取多语种视频列表失败');
          setApiData({
            data: [],
            pagination: {
              page: 1,
              per_page: 10,
              total: 0,
              pages: 0,
              has_next: false,
              has_prev: false,
              sort: "alphabet"
            }
          });
          return;
        }
      }

      // 接口规范：seriesId 为首页点击的剧集 ID，langCode 为内层 seriesListIn 页点击的语言 code
      const langCode = urlLangCode || "en";
      
      // 将 sortBy 映射到 sort 参数：time -> 1 (按创建时间倒序), alphabet -> 2 (按名称升序)
      const sortValue = sortBy === 'time' ? 1 : 2;
      // 传递搜索关键词（如果有）
      const searchName = searchQuery.trim() || undefined;

      const data = await getTaskListApi({
        seriesId: parseInt(seriesId),
        langCode,
        sort: sortValue,
        name: searchName,
      });

      // 统一处理封面地址：如果是相对路径（如 middlestage/...png），补上存储前缀
      // 图片不是内容哈希资源，使用 /storage 路径（不使用强缓存）
      const storagePrefix = getVideoStoragePrefix();

      const processedData: Task.GetTaskListResult = {
        ...data,
        data: (data.data || []).map((item: any) => {
          const rawCover: string | null | undefined = item.cover_url || item.coverUrl || null;
          let coverUrl = rawCover;

          if (rawCover) {
            if (rawCover.startsWith('http://') || rawCover.startsWith('https://')) {
              coverUrl = rawCover;
            } else {
              // 为相对路径补上存储前缀（图片使用 /storage 路径）
              const cleanPath = rawCover.startsWith('/') ? rawCover : `/${rawCover}`;
              coverUrl = `${storagePrefix}${cleanPath}`;
            }
          }

          return {
            ...item,
            cover_url: coverUrl,
            coverUrl: coverUrl,
          };
        }),
      };

      setApiData(processedData);

    } catch (error) {
      message.error('获取数据失败');
      setApiData({
        data: [],
        pagination: {
          page: 1,
          per_page: 10,
          total: 0,
          pages: 0,
          has_next: false,
          has_prev: false,
          sort: "alphabet"
        }
      });
    } finally {
      setIsLoading(false);
    }
  }, [seriesId, searchQuery, sortBy, isPure, urlLangCode]);

  // 获取说话人数据
  const fetchSpeakersData = useCallback(async () => {
    if (!seriesId) return;
    // 后端要求必须带 langCode；缺少则不调用该接口
    if (!urlLangCode) return;

    try {
      setSpeakersLoading(true);
      const response = await getSeriesSpeakersApi(parseInt(seriesId), urlLangCode);

      // 处理不同的响应格式
      let speakersData: Speakers.Speaker[] = [];

      // 情况1: 标准格式 {success: true, data: [...]}
      if (response && typeof response === 'object' && 'success' in response) {
        if (response.success && Array.isArray(response.data)) {
          speakersData = response.data;
        }
      }
      // 情况2: 直接返回数组格式
      else if (Array.isArray(response)) {
        speakersData = response;
      }
      // 情况3: 其他格式
      else {
        speakersData = [];
      }

      setSpeakersData(speakersData);
    } catch (error) {
      console.error('获取说话人数据出错:', error);
      setSpeakersData([]);
    } finally {
      setSpeakersLoading(false);
    }
  }, [seriesId, urlLangCode]);

  // 删除自动刷新任务状态的useEffect
  // useEffect(() => {
  //   if (!autoRefreshEnabled) return;

  //   const interval = setInterval(() => {
  //     const hasProcessingTasks = apiData.data?.some(item => 
  //       item.status === 0 || item.status === 1 || item.status === 4
  //     );

  //     if (hasProcessingTasks) {
  //       console.log('自动刷新任务状态...');
  //       refresh();
  //     }
  //   }, refreshInterval);

  //   return () => clearInterval(interval);
  // }, [autoRefreshEnabled, refreshInterval, apiData.data, refresh]);

  useEffect(() => {
    const getSeriesInfo = async () => {
      if (!seriesId) {
        setIsLoadingSeriesInfo(false);
        return;
      }

      try {
        setIsLoadingSeriesInfo(true);
        // 使用新的剧集详情接口单独获取剧集信息
        const response = await getSeriesDetailApi(parseInt(seriesId));



        const targetSeries = response.data;

        if (targetSeries) {
          // 统一使用新字段 needRemovalSubtitle，保证和接口展示一致
          const rawNeedRemovalSubtitle = targetSeries.needRemovalSubtitle;


          // 直接将接口返回的 needRemovalSubtitle 转为布尔值
          const needRemovalSubtitle = !!rawNeedRemovalSubtitle;



          setCurrentEpisodeInfo({
            name: targetSeries.name,
            // 源语言：使用详情接口返回的 sourceLangCode
            sourceLanguage: targetSeries.sourceLangCode || 'zh',
            // 目标语言：当前详情接口未返回，保留原有值或给默认占位
            targetLanguage: currentEpisodeInfo.targetLanguage || 'en',
            needRemovalSubtitle
          });

          // 设置角色信息
          if (targetSeries.characterProfiles && targetSeries.characterProfiles.length > 0) {
            setCharacterProfiles(targetSeries.characterProfiles);
          } else {
            setCharacterProfiles([]);
          }

        }
      } catch (error) {
        console.error('获取剧集信息失败:', error);
      } finally {
        setIsLoadingSeriesInfo(false);
      }
    };

    getSeriesInfo();
  }, [seriesId]);

  useEffect(() => {
    if (seriesId) {
      // 刷新任务列表
      refresh();
      // 暂时停用说话人数据接口，避免后端未就绪导致报错
      // fetchSpeakersData();
    }
  }, [refresh, seriesId]);


  // 已移除：页面获得焦点/可见时的自动刷新逻辑，避免频繁请求

  // 准备数据（搜索和排序已由后端处理）
  const videoItems = (apiData.data && apiData.data.length > 0)
    ? apiData.data.map(video => ({
      ...video,
      type: 'video' as const,
      img: video.cover_url,
      resultUrl: video.result_url,
      createTime: video.create_time,
      status: video.status
    }))
    : [];

  // 使用后端返回的数据，不再进行前端排序
  const sortedVideos = videoItems;

  // 返回剧集列表页（对应内层剧集页）
  const goToSeriesList = () => {
    // 如果有当前剧集信息，则返回对应的内层剧集页
    if (seriesId) {
      navigate(
        `/seriesListIn?seriesId=${seriesId}&seriesName=${encodeURIComponent(
          seriesName || currentEpisodeInfo.name || ""
        )}`
      );
    } else {
      // 没有剧集ID时回退到外层剧集页
      navigate('/home');
    }
  };


  const handleSortChange = (value: 'time' | 'alphabet') => {
    setSortBy(value);
  };

  // 搜索输入处理函数（带防抖优化）
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // 立即更新输入框显示的值，保证UI响应流畅
    setSearchInputValue(value);

    // 清除之前的定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // 设置新的防抖定时器，延迟更新 searchQuery 触发API调用
    debounceTimerRef.current = setTimeout(() => {
      setSearchQuery(value);
      debounceTimerRef.current = null;
    }, 1000); // 1秒防抖延迟
  };

  // 组件卸载时清除定时器
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // 同步 searchQuery 到 searchInputValue（防御性处理，确保状态一致）
  useEffect(() => {
    // 只有在 searchQuery 不是由用户输入触发时才同步
    // 如果存在防抖定时器，说明用户正在输入，不进行同步
    if (!debounceTimerRef.current) {
      setSearchInputValue(searchQuery);
    }
  }, [searchQuery]);

  // 双击编辑相关函数
  const handleNameEditStart = (e: React.MouseEvent, itemId: number, currentName: string) => {
    e.stopPropagation();
    setEditingItems(prev => new Set(prev).add(itemId));
    setEditingNames(prev => ({ ...prev, [itemId]: currentName }));
  };

  const handleNameEditChange = (itemId: number, newName: string) => {
    setEditingNames(prev => ({ ...prev, [itemId]: newName ?? '' }));
  };




  const handleNameEditSave = async (itemId: number) => {
    const newName = editingNames[itemId];
    if (!newName || !newName.trim()) {
      setEditingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
      setEditingNames(prev => {
        const newNames = { ...prev };
        delete newNames[itemId];
        return newNames;
      });
      return;
    }

    // 获取原始名字
    const originalItem = apiData.data.find(video => video.id === itemId);
    const originalName = originalItem?.name || '';
    const trimmedNewName = newName.trim();

    // 如果名字没有变化，直接退出编辑状态，不发送请求
    if (originalName === trimmedNewName) {
      setEditingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
      setEditingNames(prev => {
        const newNames = { ...prev };
        delete newNames[itemId];
        return newNames;
      });
      return;
    }

    try {
      // 调用更新视频名称的API
      await putNameTaskApi(itemId, trimmedNewName);

      setApiData(prev => ({
        ...prev,
        data: prev.data.map(video =>
          video.id === itemId
            ? { ...video, name: trimmedNewName }
            : video
        )
      }));

      setEditingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
      setEditingNames(prev => {
        const newNames = { ...prev };
        delete newNames[itemId];
        return newNames;
      });

      showSuccess('重命名成功');
    } catch (error) {
      showError('重命名失败，请稍后重试');
    }
  };

  // 删除页面切换处理函数
  // const handlePageChange = (page: number) => {
  //   setCurrentPage(page);
  // };

  // 添加手动刷新按钮的处理函数
  const handleRefreshTask = async (taskId: number) => {
    try {
      await refresh();
      message.success('任务状态已刷新');
    } catch (error) {
      message.error('刷新失败，请重试');
    }
  };

  // 从URL获取File对象的辅助函数
  const urlToFile = async (url: string, filename: string): Promise<File> => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], filename, { type: blob.type });
  };

  // 刷新重传失败视频的处理函数（使用新的 retry 接口）
  const handleReuploadTask = async (taskId: number, taskName: string) => {
    try {
      // 显示重新上传中的提示
      showInfo('正在重新上传视频，请稍候...');

      // 调用刷新重传接口
      const result = await retryTaskApi(taskId);

      if (result && result.code === 0 && result.data) {
        // 刷新列表以获取最新状态
        await refresh();
        showSuccess(`视频 "${taskName}" 刷新重传成功`);
      } else {
        showError(result?.msg || '刷新重传失败，请稍后重试');
      }
    } catch (error: any) {
      console.error('重新上传失败:', error);

      // 构建详细的错误信息
      let errorMessage = '重新上传失败，请稍后重试';

      if (error?.response?.data?.info) {
        errorMessage = error.response.data.info;
      } else if (error?.response?.message) {
        errorMessage = error.response.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showError(errorMessage);
    }
  };

  // 删除视频
  const deleteVideo = async (taskId: number, taskName: string) => {
    try {
      await delTaskApi(taskId);
      showSuccess(`视频 "${taskName}" 删除成功`);
      await refresh();
    } catch (error) {
      showError('删除视频失败，请稍后重试');
    }
  };

  // 批量删除视频
  const handleBatchDelete = async () => {
    if (selectedVideoIds.length === 0) {
      showWarning('请先选择要删除的视频');
      return;
    }

    try {
      showInfo('正在批量删除视频，请稍候...');
      const result = await batchDelTaskApi(selectedVideoIds);

      if (result && result.code === 0 && result.data) {
        await refresh();
        setSelectedVideoIds([]);
        showSuccess(`已成功删除 ${selectedVideoIds.length} 个视频`);
      } else {
        showError(result?.msg || '批量删除失败，请稍后重试');
      }
    } catch (error: any) {
      console.error('批量删除失败:', error);
      let errorMessage = '批量删除失败，请稍后重试';
      if (error?.response?.data?.msg) {
        errorMessage = error.response.data.msg;
      } else if (error?.response?.message) {
        errorMessage = error.response.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      showError(errorMessage);
    }
  };

  // 添加下载视频功能
  const handleDownload = (item: any) => {
    const videoUrl = getVideoPlayUrl(item);
    if (videoUrl) {
      const link = document.createElement('a');
      link.href = videoUrl;
      // 生成安全的文件名，移除特殊字符
      const safeFileName = item.name.replace(/[<>:"/\\|?*]/g, '_');
      link.download = `${safeFileName}.mp4`;
      link.click();
      message.success('开始下载视频');
    } else {
      message.error('视频链接不可用');
    }
  };

  // 批量下载视频功能
  const handleBatchDownload = async () => {
    if (selectedVideoIds.length === 0) {
      showWarning('请先选择要下载的视频');
      return;
    }

    // 获取选中的视频数据
    const selectedVideos = sortedVideos.filter(item => selectedVideoIds.includes(item.id));
    
    // 检查是否有可下载的视频
    const downloadableVideos = selectedVideos.filter(item => {
      const videoUrl = getVideoPlayUrl(item);
      return videoUrl && item.status === 2; // 只下载已完成状态的视频
    });

    if (downloadableVideos.length === 0) {
      showError('没有可下载的视频，请确保视频已完成处理');
      return;
    }

    try {
      showInfo(`正在准备下载 ${downloadableVideos.length} 个视频，请稍候...`);

      // 构建文件夹名：剧集名_语种_全集
      const folderNameParts: string[] = [];
      
      // 剧集名
      const finalSeriesName = seriesName || currentEpisodeInfo.name || '未命名剧集';
      folderNameParts.push(finalSeriesName);
      
      // 语种（优先使用视频数据中的langCode，其次使用URL参数中的urlLangCode）
      const rawLangCode = downloadableVideos[0]?.sourceLanguage || urlLangCode;
      if (rawLangCode) {
        const langCode = String(rawLangCode).toLowerCase().trim();
        // 直接使用语言映射表，确保转换正确
        const languageMap: Record<string, string> = {
          'en': '英语',
          'zh': '中文',
          'ja': '日语',
          'de': '德语',
          'fr': '法语',
          'es': '西班牙语',
          'ko': '韩语',
          'ar': '阿拉伯语',
          'ru': '俄语',
          'nl': '荷兰语',
          'it': '意大利语',
          'pl': '波兰语',
          'pt': '葡萄牙语',
          'vi': '越南语',
          'id': '印尼语'
        };
        const languageName = languageMap[langCode] || langCode;
        folderNameParts.push(languageName);
      }
      
      // 全集
      folderNameParts.push('全集');
      
      // 生成安全的文件夹名，移除特殊字符
      const safeFolderName = folderNameParts.join('_').replace(/[<>:"/\\|?*]/g, '_');
      
      console.log('文件夹名构建:', { folderNameParts, safeFolderName });
      
      // 创建JSZip实例
      const zip = new JSZip();
      const folder = zip.folder(safeFolderName);
      
      if (!folder) {
        showError('创建下载文件夹失败');
        return;
      }

      // 下载所有视频并添加到zip
      const downloadPromises = downloadableVideos.map(async (item) => {
        try {
          const videoUrl = getVideoPlayUrl(item);
          if (!videoUrl) {
            console.warn(`视频 ${item.name} 的URL不可用`);
            return null;
          }

          // 使用fetch下载视频
          const response = await fetch(videoUrl);
          if (!response.ok) {
            throw new Error(`下载失败: ${response.statusText}`);
          }
          
          const blob = await response.blob();
          
          // 生成安全的文件名
          const safeFileName = item.name.replace(/[<>:"/\\|?*]/g, '_');
          const fileName = `${safeFileName}.mp4`;
          
          // 添加到zip文件夹
          folder.file(fileName, blob);
          
          return fileName;
        } catch (error: any) {
          console.error(`下载视频 ${item.name} 失败:`, error);
          return null;
        }
      });

      // 等待所有下载完成
      const results = await Promise.all(downloadPromises);
      const successCount = results.filter(r => r !== null).length;

      if (successCount === 0) {
        showError('所有视频下载失败，请检查网络连接或视频链接');
        return;
      }

      // 生成zip文件
      showInfo('正在打包文件...');
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // 创建下载链接
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeFolderName}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (successCount < downloadableVideos.length) {
        showWarning(`成功下载 ${successCount}/${downloadableVideos.length} 个视频，部分视频下载失败`);
      } else {
        showSuccess(`成功下载 ${successCount} 个视频`);
      }
    } catch (error: any) {
      console.error('批量下载失败:', error);
      showError(error?.message || '批量下载失败，请稍后重试');
    }
  };



  // 添加播放视频功能 - 弹窗播放
  const handlePlay = (item: any) => {
    console.log('handlePlay called with item:', item);
    if (item.status === 2) {
      const videoUrl = getVideoPlayUrl(item);
      console.log('原始URL字段:', {
        result_url: item.result_url,
        pure_video_url: item.pure_video_url,
        file_url: item.file_url
      });
      console.log('处理后的videoUrl:', videoUrl);

      if (videoUrl) {
        const videoData = {
          url: videoUrl,
          name: item.name,
          status: item.status.toString(),
          createTime: item.create_time || '',
          message: item.descript || '',
          id: item.id.toString()
        };
        console.log('设置视频数据:', videoData);
        setCurrentVideoData(videoData);
        setVideoModalVisible(true);
      } else {
        message.error('视频链接不可用');
      }
    } else {
      message.info('视频正在处理中，请稍后再试');
    }
  };

  // 关闭视频播放弹窗
  const handleCloseVideoModal = () => {
    setVideoModalVisible(false);
    setCurrentVideoData(null);
  };

  // 底部操作按钮事件处理
  const handleActionButtonClick = (videoId: number) => {
    setActionMenu({
      visible: !actionMenu.visible,
      videoId: actionMenu.visible ? null : videoId
    });
  };

  // 关闭操作菜单
  const closeActionMenu = () => {
    setActionMenu({
      visible: false,
      videoId: null
    });
  };

  // 菜单操作处理
  const handleMenuAction = async (action: 'rework' | 'approve' | 'submit' | 'revoke') => {
    if (!actionMenu.videoId) return;

    const video = apiData.data?.find(item => item.id === actionMenu.videoId);
    if (!video) return;

    if (action === 'rework') {
      // 审核者：需返工
      try {
        // 调用后端审核接口，approve=false 表示返工
        const response = await approveTaskApi(video.id, false);

        if (response.code !== 0 || !response.data) {
          // 使用全局 AlertMessage 提示后端返回的错误信息
          showError(response.msg || '需返工操作失败，请重试');
          return;
        }

        // 返工成功后，本地 tag 固定更新为 2
        const newTag = 2;

        setApiData(prev => ({
          ...prev,
          data: prev.data.map(item =>
            item.id === video.id
              ? { ...item, tag: newTag }
              : item
          )
        }));

        message.success(`已将视频"${video.name}"标记为需返工`);
      } catch (error: any) {
        console.error('需返工操作失败:', error);
        const errorMsg = error?.msg || error?.message || '需返工操作失败，请重试';
        // 用 AlertMessage 的错误提示替代 message.error
        showError(errorMsg);
      }
    } else if (action === 'approve') {
      // 审核者：审核通过
      try {
        // 调用后端审核接口，approve=true 表示审核通过
        const response = await approveTaskApi(video.id, true);

        if (response.code !== 0 || !response.data) {
          message.error(response.msg || '审核通过操作失败，请重试');
          return;
        }

        // 审核通过成功后，本地 tag 固定更新为 1
        const newTag = 1;

        setApiData(prev => ({
          ...prev,
          data: prev.data.map(item =>
            item.id === video.id
              ? { ...item, tag: newTag }
              : item
          )
        }));

        message.success(`已将视频"${video.name}"标记为审核通过`);
      } catch (error: any) {
        console.error('审核通过操作失败:', error);
        const errorMsg = error?.msg || error?.message || '审核通过操作失败，请重试';
        message.error(errorMsg);
      }
    } else if (action === 'submit') {
      // 提交操作 - 操作者点击提交按钮
      try {
        // 使用后端新的提交审核接口，由后端完成 tag 更新和通知发送
        const response = await submitForReviewApi(video.id);

        if (response.code !== 0) {
          message.error(response.msg || '提交操作失败，请重试');
          return;
        }

        const newTag = typeof response.data === 'number' ? response.data : Number(video.tag ?? 0);

        // 使用后端返回的 tag 值更新本地状态
        setApiData(prev => ({
          ...prev,
          data: prev.data.map(item =>
            item.id === video.id
              ? { ...item, tag: newTag }
              : item
          )
        }));

        message.success(`视频"${video.name}"已提交`);
      } catch (error: any) {
        console.error('提交操作失败:', error);
        const errorMsg = error?.msg || error?.message || '提交操作失败，请重试';
        message.error(errorMsg);
      } finally {
        // 视频提交操作完成后，无论成功失败都尝试刷新积分
        window.dispatchEvent(new Event('points:refresh'));
      }
    } else if (action === 'revoke') {
      // 撤销操作 - 操作者点击撤销按钮
      try {
        // 调用后端撤销接口，由后端完成业务和通知逻辑
        const response = await revokeSubmissionApi(video.id);

        if (response.code !== 0) {
          message.error(response.msg || '撤销操作失败，请重试');
          return;
        }

        // 撤销成功后，前端本地 tag 固定更新为 0（未提交）
        const newTag = 0;

        setApiData(prev => ({
          ...prev,
          data: prev.data.map(item =>
            item.id === video.id
              ? { ...item, tag: newTag }
              : item
          )
        }));

        message.success(`视频"${video.name}"已撤销`);

      } catch (error: any) {
        console.error('撤销操作失败:', error);
        const errorMsg = error?.msg || error?.message || '撤销操作失败，请重试';
        message.error(errorMsg);
      }
    }

    closeActionMenu();
  };

  // 打开一键导入弹窗
  const handleOpenImportModal = async () => {
    if (!seriesId || !urlLangCode) {
      message.warning('缺少必要参数');
      return;
    }

    setLoadingMissingTasks(true);

    try {
      const response = await getMissingTasksApi(parseInt(seriesId), urlLangCode);
      if (response.code === 0 && response.data) {
        // 只显示 status === 2 的数据
        const filteredData = response.data.filter(task => task.status === 2);
        // 如果没有需要导入的视频，使用 AlertMessage 警告色提示，不打开弹窗
        if (filteredData.length === 0) {
          showWarning('暂无需要导入的视频');
          return;
        }
        // 有数据时才打开弹窗
        setMissingTasks(filteredData);
        setImportModalVisible(true);
      } else {
        message.error(response.msg || '获取缺失任务列表失败');
      }
    } catch (error: any) {
      console.error('获取缺失任务列表失败:', error);
      message.error(error?.message || '获取缺失任务列表失败');
    } finally {
      setLoadingMissingTasks(false);
    }
  };

  // 关闭一键导入弹窗
  const handleCloseImportModal = () => {
    setImportModalVisible(false);
    setMissingTasks([]);
  };

  // 删除视频标签
  const handleRemoveVideoTag = (index: number) => {
    setMissingTasks(prev => prev.filter((_, i) => i !== index));
  };

  // 确认导入
  const handleConfirmImport = async () => {
    if (missingTasks.length === 0) {
      showWarning('没有可导入的视频');
      return;
    }

    if (!seriesId || !urlLangCode) {
      showError('缺少必要参数');
      return;
    }

    try {
      // 获取留下的视频的 id 列表
      const taskIds = missingTasks.map(task => task.id);

      // 调用导入接口
      const response = await importTasksApi({
        seriesId: parseInt(seriesId),
        langCode: urlLangCode, // 目标语种代码（字符串）
        taskIds: taskIds, // 任务ID列表（数组格式，如 [85,84]）
        // transTerms 和 aiVoiceover 为可选参数，如果需要可以在调用时传入
      });

      if (response.code === 0) {
        // 成功：使用AlertMessage提示
        showSuccess('导入成功');
        handleCloseImportModal();
        // 刷新视频列表以同步内容
        await refresh();
      } else {
        // 失败：显示响应中的msg内容
        showError(response.msg || '导入失败');
      }
    } catch (error: any) {
      console.error('导入失败:', error);
      // 失败：显示错误信息
      let errorMessage = '导入失败，请稍后重试';
      if (error?.response?.data?.msg) {
        errorMessage = error.response.data.msg;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      showError(errorMessage);
    }
  };

  if (!seriesId) {
    // if (!seriesId || !seriesName) {
    return (
      <div className="error-container">
        <div className="error-content">
          <div className="error-icon">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <div className="error-text">剧集信息不完整</div>
          <Button type="primary" onClick={goToSeriesList}>
            返回剧集列表
          </Button>
        </div>
      </div>
    );
  }

  // 切换批量操作按钮显示
  const toggleBatchActions = () => {
    setShowBatchActions(prev => !prev);
  };

  // 全选 / 取消全选
  const handleSelectAllChange: CheckboxProps['onChange'] = (e) => {
    const checked = e.target.checked;
    if (checked) {
      const allIds = sortedVideos.map((item) => item.id);
      setSelectedVideoIds(allIds);
    } else {
      setSelectedVideoIds([]);
    }
  };

  // 单个视频选中 / 取消
  const handleVideoSelectToggle = (videoId: number) => {
    setSelectedVideoIds((prev) => {
      if (prev.includes(videoId)) {
        return prev.filter((id) => id !== videoId);
      }
      return [...prev, videoId];
    });
  };

  const isAllSelected = sortedVideos.length > 0 && selectedVideoIds.length === sortedVideos.length;
  const isIndeterminate = selectedVideoIds.length > 0 && !isAllSelected;
  return (
    <>
      <div className="main-content">
        <AlertMessage
          config={alertConfig}
          onClose={hideAlert}
          zIndex={ALERT_Z_INDEX.MY_WORK}
        />

        {/* 搜索和操作栏 */}
        <div className="search-filter-bar">
          <div className="back-to-root">
            <BackButton onClick={goToSeriesList} />
            {(() => {
              // 如果URL中的seriesName是"获取失败"，则不显示它
              // 优先使用currentEpisodeInfo.name（从接口获取的），如果也没有则显示其他有效的seriesName
              let displayName: string | null = null;
              
              if (seriesName === '获取失败') {
                // 如果seriesName是"获取失败"，只使用currentEpisodeInfo.name
                displayName = currentEpisodeInfo.name || null;
              } else if (seriesName) {
                // 如果seriesName有效，优先使用它
                displayName = seriesName;
              } else {
                // 如果seriesName为空，使用currentEpisodeInfo.name
                displayName = currentEpisodeInfo.name || null;
              }
              
              // 如果是多语种视频列表，在剧集名称后拼接"—语种"
              if (displayName && urlLangCode) {
                const languageName = getLanguageName(urlLangCode);
                displayName = `${displayName}—${languageName}`;
              }
              
              return displayName && (
                <span className="series-name">{displayName}</span>
              );
            })()}
          </div>

          <div className="search-sort-container">
            <div className="search-section">
              <div className="search-bar">
                <i className="fas fa-search search-icon"></i>
                <Input
                  placeholder="搜索视频"
                  className="search-input show"
                  value={searchInputValue}
                  onChange={handleSearchChange}
                />
              </div>
            </div>

            <div className="sort-section">
              <Select
                value={sortBy}
                onChange={handleSortChange}
                className="sort-dropdown"
                placeholder="排序方式"
              >
                <Select.Option value="time">按时间排序</Select.Option>
                <Select.Option value="alphabet">按名称排序</Select.Option>
              </Select>
            </div>
          </div>

          <div className="action-section">
            {/* 上传视频按钮 - 纯净版视频列表使用 - 需要video:clean:upload权限 */}
            {hasUploadVideoPermission() && isPure && (
              <Button
                type="primary"
                className="upload-video-btn"
                onClick={() => {
                  // 传递剧集ID和名称到上传页面，并标记为纯净版
                  if (seriesId) {
                    navigate(`/uploadVideo?seriesId=${seriesId}&seriesName=${encodeURIComponent(seriesName || '')}&isPure=true`);
                  } else {
                    navigate("/uploadVideo?isPure=true");
                  }
                }}
              >
                <i className="fas fa-upload"></i>
                上传视频
              </Button>
            )}

            {/* 一键导入按钮 - 普通视频列表使用 - 需要video:multilingual:import权限 */}
            {hasImportVideoPermission() && !isPure && (
              <Button
                type="primary"
                className="upload-video-btn"
                onClick={handleOpenImportModal}
              >
                <i className="fas fa-upload"></i>
                一键导入
              </Button>
            )}
          </div>

          <EpisodeDrawer
            episodeInfo={currentEpisodeInfo}
            speakersData={speakersData}
            speakersLoading={speakersLoading}
            seriesName={seriesName}
            characterProfiles={characterProfiles}
          />
        </div>

        {isLoading ? (
          <div className="videos-grid-out">
            <div className="skeleton-grid">
              <VideoCardSkeleton count={10} />
            </div>
          </div>
        ) : sortedVideos.length === 0 ? (
          <div className="empty-search-container">
            <div className="empty-search-content">
              <div className="empty-icon">
                <i className="fas fa-video"></i>
              </div>
              <div className="empty-text">
                暂无视频
              </div>
            </div>
          </div>
        ) : (
          <div className="videos-grid-out">
            <div className="select-all-checkbox">
              <div className="select-all-checkbox-left">
                {showBatchActions && (
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={handleSelectAllChange}
                  >
                    全选
                  </Checkbox>
                )}
                <span>（已传:{sortedVideos.length}）</span>
              </div>
              <div className="select-all-checkbox-right">
                {/* 从多语种进入的视频列表页不显示"新增语种"按钮 */}
                {!(urlLangCode && !isPure) && (
                  <span
                    className="batch-action-item show"
                    onClick={() => {
                      // 检查是否选中了视频
                      if (!selectedVideoIds || selectedVideoIds.length === 0) {
                        showWarning('您未选中视频，请选中后操作');
                        return;
                      }
                      
                      // 纯净版视频：只有任务状态为完成(2)时才允许新增语种
                      // 只检查用户选中的视频，而不是所有视频
                      if (isPure) {
                        const selectedVideos = sortedVideos.filter(item => selectedVideoIds.includes(item.id));
                        const failedVideos = selectedVideos.filter(item => item.status === 3);
                        const unfinishedVideos = selectedVideos.filter(item => item.status !== 2 && item.status !== 3);
                        if (failedVideos.length > 0 || unfinishedVideos.length > 0) {
                          const failedNames = failedVideos.map(v => v.name).join('、');
                          const unfinishedNames = unfinishedVideos.map(v => v.name).join('、');
                          const parts: string[] = [];
                          if (failedNames) {
                            parts.push(`检测到${failedNames}视频失败，请处理完成后再新增语种`);
                          }
                          if (unfinishedNames) {
                            parts.push(`检测到${unfinishedNames}视频未完成，请处理完成后再新增语种`);
                          }
                          showError(parts.join('；'));
                          return;
                        }
                      }
                      setAddLanguageModalVisible(true);
                      setAiVoiceEnabled(true); // 默认打开 AI 配音
                    }}
                  >
                    新增语种
                  </span>
                )}
                {hasBatchDownloadPermission() && (
                  <span
                    className="batch-action-item show"
                    onClick={() => {
                      // 检查是否选中了视频
                      if (!selectedVideoIds || selectedVideoIds.length === 0) {
                        showWarning('请先选择要下载的视频');
                        return;
                      }
                      
                      // 纯净版视频：只有任务状态为完成(2)时才允许下载
                      // 只检查用户选中的视频，而不是所有视频
                      if (isPure) {
                        const selectedVideos = sortedVideos.filter(item => selectedVideoIds.includes(item.id));
                        const failedVideos = selectedVideos.filter(item => item.status === 3);
                        const unfinishedVideos = selectedVideos.filter(item => item.status !== 2 && item.status !== 3);
                        if (failedVideos.length > 0 || unfinishedVideos.length > 0) {
                          const failedNames = failedVideos.map(v => v.name).join('、');
                          const unfinishedNames = unfinishedVideos.map(v => v.name).join('、');
                          const parts: string[] = [];
                          if (failedNames) {
                            parts.push(`${failedNames}视频失败`);
                          }
                          if (unfinishedNames) {
                            parts.push(`${unfinishedNames}视频未完成`);
                          }
                          showError(parts.join('；'));
                          return;
                        }
                      }
                      // 调用批量下载函数
                      handleBatchDownload();
                    }}
                  >
                    下载
                  </span>
                )}
                {hasBatchDeletePermission() && (
                  <Popconfirm
                    title={`请确认是否批量删除所选的${selectedVideoIds.length}个视频，删除后将不可恢复，已消耗的积分也无法退还。`}
                    onConfirm={handleBatchDelete}
                    okText="确定"
                    cancelText="取消"
                    overlayStyle={{ maxWidth: 315 }}
                  >
                    <span
                      className="batch-action-item show"
                    >
                      删除
                    </span>
                  </Popconfirm>
                )}
                <span className="batch-toggle" onClick={toggleBatchActions}>
                  <img src="/img/piliang.png" alt="批量" />
                </span>
              </div>

            </div>
            <div className="videos-grid">
              {sortedVideos.map((item) => {
                const isSelected = selectedVideoIds.includes(item.id);
                return (
                  <div key={`${item.type}-${item.id}`} className="video-card-wrapper">
                    <div
                      className={`video-card ${actionMenu.visible && actionMenu.videoId === item.id ? 'bubble-menu-active' : ''} ${isSelected ? 'selected' : ''}`}
                      data-tag={item.tag || 0}
                    >
                      {/* 左上角选择勾选框（由批量开关控制显示/隐藏） */}
                      {showBatchActions && (
                        <div
                          className="video-select-checkbox"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVideoSelectToggle(item.id);
                          }}
                        >
                          <Checkbox checked={isSelected} />
                        </div>
                      )}

                      {/* 视频缩略图 */}
                      <div className="video-thumbnail">
                        {/* 纯净版且 tag === 5 时显示“已校对”角标 */}
                        {isPure && item.tag === 5 && (
                          <img
                            src="/img/jiaodui.png"
                            alt="已校对"
                            className="pure-proofread-badge"
                          />
                        )}
                        <div
                          className="background-image"
                          style={{
                            backgroundImage: `url(${item.cover_url || '/api/video/thumbnail/default.jpg'})`
                          }}
                        ></div>

                        <img
                          src={item.cover_url || '/api/video/thumbnail/default.jpg'}
                          alt={item.name}
                          className="thumbnail-image"
                        />

                        <div
                          className="play-icon-overlay"
                          onClick={() => {
                            // 只有状态为2（已完成）的视频才能进入编辑页面
                            if (item.status === 2) {
                              // 如果视频已审核通过（tag = 1），则只有操作者不能进入编辑页
                              // 管理员和审核者不受限制
                              if (item.tag === 1 && isOperatorOnly) {
                                showWarning('视频已通过审核，不可再进行编辑');
                                return;
                              }

                              // 获取 seriesId 参数并传递到视频编辑页
                              const currentSeriesId = searchParams.get('seriesId');
                              const isPure = searchParams.get('isPure') === 'true';
                              const currentSeriesName = searchParams.get('seriesName');
                              const currentLangCode = searchParams.get('langCode');

                              // 保留从列表页进入时的上下文参数，确保从编辑页返回列表页时“语种”不丢失
                              const queryParts: string[] = [];
                              if (currentSeriesId) queryParts.push(`seriesId=${encodeURIComponent(currentSeriesId)}`);
                              if (currentSeriesName) queryParts.push(`seriesName=${encodeURIComponent(currentSeriesName)}`);
                              if (currentLangCode) queryParts.push(`langCode=${encodeURIComponent(currentLangCode)}`);
                              if (isPure) queryParts.push('isPure=true');

                              const navigateUrl = queryParts.length > 0
                                ? `/videoEdit/${item.id}?${queryParts.join('&')}`
                                : `/videoEdit/${item.id}`;
                              navigate(navigateUrl, {
                                state: { 
                                  videoName: item.name,
                                  isPure: isPure // 传递是否从纯净版进入的状态
                                }
                              });
                            } else if (item.status === 3) {
                              // 失败状态的视频显示红色提示信息
                              showError('视频生成失败，请刷新重传或删除');
                            } else if (item.status === 4) {
                              // 排队中的视频显示提示信息
                              showInfo('视频正在排队中，请稍后再试');
                            } else {
                              // 处理中的视频显示提示信息
                              showInfo('视频正在处理中，请稍后再试');
                            }
                            // handlePlay(item);
                          }}
                          style={{
                            cursor: item.status === 2 ? 'pointer' : 'not-allowed',
                            opacity: item.status === 2 ? 1 : 0.6
                          }}
                          title={
                            item.status === 2
                              ? (item.tag === 1 && isOperatorOnly
                                  ? '视频已通过审核，不可再进行编辑'
                                  : '点击编辑视频')
                              : item.status === 3
                                ? '视频生成失败，请刷新重传或删除'
                                : item.status === 4
                                  ? '视频排队中，暂不可进行编辑'
                                  : '视频处理中，暂不可进行编辑'
                          }
                        >
                          {item.status === 3 ? (
                            // 上传失败状态显示红色感叹号图片
                            <img
                              src="/img/yichang.png"
                              alt="上传失败"
                              style={{
                                width: '20px',
                                height: '20px',
                                marginLeft: '2px'
                              }}
                              aria-label="上传失败"
                            />
                          ) : (
                            <img
                              src={item.status === 0 || item.status === 1 || item.status === 4 ? "/img/shalou.png" : "/img/edit.png"}
                              alt="status icon"
                              style={{
                                width: '20px',
                                height: '20px',
                                color: '#333333',
                                marginLeft: '2px',
                                fontWeight: '400'
                              }}
                            />
                          )}
                          {/* <i className={item.status === 0 || item.status === 1 ? "icon-shalou iconfont" : "iconfont icon-bofang"}></i> */}
                        </div>
                        <div className="play-duration-overlay">
                          <i className="iconfont icon-bofang"></i>
                          <span style={{ fontWeight: 600 }}>
                            {formatDuration(
                              typeof (item as any).duration === 'number'
                                ? (item as any).duration as number
                                : Number((item as any).duration) || 0
                            )}
                          </span>
                        </div>

                        <div className="status-overlay">
                          {item.status === 2 ? '已完成' :
                            item.status === 0 ? '等待' :
                              item.status === 1 ? '处理中' :
                                item.status === 3 ? '失败' :
                                  item.status === 4 ? '排队中' : '未知'}
                        </div>
                      </div>

                      {/* 视频名称 */}
                      <div className="video-name">
                        {editingItems.has(item.id) ? (
                          <Input
                            value={editingNames[item.id] ?? ''}
                            onChange={(e) => handleNameEditChange(item.id, e.target.value)}
                            onPressEnter={() => handleNameEditSave(item.id)}
                            onBlur={() => handleNameEditSave(item.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') {
                                setEditingItems(prev => {
                                  const newSet = new Set(prev);
                                  newSet.delete(item.id);
                                  return newSet;
                                });
                                setEditingNames(prev => {
                                  const newNames = { ...prev };
                                  delete newNames[item.id];
                                  return newNames;
                                });
                              }
                            }}
                            autoFocus
                            className="name-edit-input"
                          />
                        ) : (
                          <div className="name-with-edit">
                            <span
                              onDoubleClick={(e) => handleNameEditStart(e, item.id, item.name)}
                              className="name-text"
                            >
                              {item.name}
                            </span>
                            <i
                              className="fas fa-edit edit-icon"
                              onClick={(e) => handleNameEditStart(e, item.id, item.name)}
                              style={{ cursor: 'pointer' }}
                            ></i>
                          </div>
                        )}
                      </div>

                      {/* 创建时间 */}
                      <div className="video-time">
                        {item.create_time ? new Date(item.create_time).toLocaleString('zh-CN', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        }).replace(/\//g, '-') : '未知时间'}
                      </div>

                      {/* 底部操作按钮 */}
                      <div className="video-actions">
                        {item.status === 2 && (
                          <>
                            {/* 视频播放按钮 - 需要videoBtn:bofang权限 - 纯净版不显示 */}
                            {hasVideoPlayPermission() && !isPure && (
                              <button
                                className="action-btn play-btn"
                                title="播放"
                                onClick={() => handlePlay(item)}
                              >
                                <i className="fas fa-play"></i>
                              </button>
                            )}

                            {/* 视频删除按钮 - 需要批量删除权限 */}
                            {hasBatchDeletePermission() && (
                              <Popconfirm
                                title="确定要删除这个视频吗？"
                                onConfirm={() => deleteVideo(item.id, item.name)}
                                okText="确定"
                                cancelText="取消"
                              >
                                <button className="action-btn delete-btn" title="删除">
                                  <i className="fas fa-trash"></i>
                                </button>
                              </Popconfirm>
                            )}
                          </>
                        )}

                        {(item.status === 0 || item.status === 1 || item.status === 4) && (
                          <>
                            {/* 视频删除按钮 - 需要批量删除权限 */}
                            {hasBatchDeletePermission() && (
                              <Popconfirm
                                title="确定要删除这个视频吗？"
                                onConfirm={() => deleteVideo(item.id, item.name)}
                                okText="确定"
                                cancelText="取消"
                              >
                                <button className="action-btn delete-btn" title="删除">
                                  <i className="fas fa-trash"></i>
                                </button>
                              </Popconfirm>
                            )}
                          </>
                        )}

                        {item.status === 3 && (
                          <>
                            {/* 视频刷新按钮 - 需要videoBtn:refresh权限 */}
                            {hasVideoRefreshPermission() && (
                              <button
                                className="action-btn refresh-btn"
                                title="刷新重传"
                                onClick={() => handleReuploadTask(item.id, item.name)}
                              >
                                <i className="fas fa-redo"></i>
                              </button>
                            )}
                            {/* 视频删除按钮 - 需要批量删除权限 */}
                            {hasBatchDeletePermission() && (
                              <Popconfirm
                                title="确定要删除这个视频吗？"
                                onConfirm={() => deleteVideo(item.id, item.name)}
                                okText="确定"
                                cancelText="取消"
                              >
                                <button className="action-btn delete-btn" title="删除">
                                  <i className="fas fa-trash"></i>
                                </button>
                              </Popconfirm>
                            )}
                          </>
                        )}

                        {/* 图钉操作按钮 - 根据角色、权限、状态和tag值判断是否显示 */}
                        {/* 纯净版视频列表不显示打标记按钮 */}
                        {!isPure && (() => {
                          const canShow = shouldShowTagButton(item.status, item.tag || 0);

                          return canShow;
                        })() && (
                            <button
                              className="action-btn pin-btn"
                              onClick={() => handleActionButtonClick(item.id)}
                              title="操作"
                            >
                              <i className="fas fa-thumbtack"></i>
                            </button>
                          )}
                      </div>
                    </div>

                    {/* 操作气泡菜单 - 在video-card外面 */}
                    {actionMenu.visible && actionMenu.videoId === item.id && (
                      <div className="action-bubble-menu">
                        <div className="action-menu-content">
                          {getTagButtonActions(item.tag || 0).map((action) => (
                            <div
                              key={action.key}
                              className="action-item"
                              onClick={() => handleMenuAction(action.key as 'rework' | 'approve' | 'submit' | 'revoke')}
                            >
                              {action.text}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}


        {/* 版权信息 */}
        {/* <div className="copyright-section">
          <div className="copyright-text">
            {COPYRIGHT_TEXT}
          </div>
        </div> */}

        {/* 视频播放弹窗 */}
        {currentVideoData && (
          <VideoPlayerModal
            visible={videoModalVisible}
            onClose={handleCloseVideoModal}
            videoUrl={currentVideoData.url}
            videoName={currentVideoData.name}
            videoStatus={currentVideoData.status}
            videoCreateTime={currentVideoData.createTime}
            videoMessage={currentVideoData.message}
            videoId={currentVideoData.id}
          />
        )}

        {/* 一键导入弹窗 */}
        <Modal
          title="导入视频"
          open={importModalVisible}
          onCancel={handleCloseImportModal}
          footer={[
            <Button key="cancel" onClick={handleCloseImportModal}>
              取消
            </Button>,
            <Button key="confirm" type="primary" onClick={handleConfirmImport}>
              确定
            </Button>,
          ]}
          width={520}
        >
          {loadingMissingTasks ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>加载中...</div>
          ) : (
            <>
              <div style={{ marginBottom: '16px', color: '#416ba9', fontWeight: 600, fontSize: '16px' }}>
                是否从{seriesName || '未知'}—纯净版中导入以下视频,生成{getLanguageName(urlLangCode || '')}配音剧?
              </div>
              {missingTasks.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                  暂无需要导入的视频
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                  {missingTasks.map((task, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '4px 12px',
                        backgroundColor: '#ffffff',
                        border: '2px solid rgba(145, 202, 255, 0.38)', // 浅一点并加粗的边框
                        borderRadius: '4px',
                        fontSize: '14px',
                        color: '#595959',
                      }}
                    >
                      <span>{task.name.replace(/\.[^/.]+$/, '')}</span>
                      <span
                        onClick={() => handleRemoveVideoTag(index)}
                        style={{
                          marginLeft: '8px',
                          cursor: 'pointer',
                          color: '#8c8c8c',
                          fontSize: '18px',
                          lineHeight: '1',
                        }}
                      >
                        ×
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Modal>

        {/* 新增语种弹窗 */}
        <Modal
          title="新增语种"
          className="add-language-modal"
          open={addLanguageModalVisible}
          onCancel={() => setAddLanguageModalVisible(false)}
          footer={[
            <Button key="cancel" onClick={() => setAddLanguageModalVisible(false)}>
              取消
            </Button>,
            <Button key="ok" type="primary" onClick={handleConfirmAddLanguage}>
              确定
            </Button>,
          ]}
          width={600}
          bodyStyle={{
            maxHeight: 480,
            overflowY: 'auto',
          }}
        >
          {/* 选择新增翻译语种 */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 600, color: '#416ba9', fontSize: '16px', padding: '10px 0 5px 0' }}>
              选择新增翻译语种<span style={{ color: '#ff4d4f' }}>*</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {availableLanguages.map((lang) => {
                const active = selectedNewLangCodes.includes(lang.code);
                return (
                  <Button
                    key={lang.code}
                    type={active ? 'primary' : 'default'}
                    size="small"
                    onClick={() => toggleNewLanguage(lang.code)}
                    style={{ padding: '4px 12px', height: 28, lineHeight: '20px' }}
                  >
                    {lang.label}
                  </Button>
                );
              })}
            </div>
          </div>

          {/* 翻译术语设置 */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 600, flex: 1, color: '#416ba9', fontSize: '16px', padding: '0px 0 5px 0' }}>翻译术语设置</span>
              <Button
                type="link"
                onClick={handleAddStyleConfigRow}
                style={{
                  padding: 0,
                  fontSize: 30,
                  fontWeight: 700,
                  lineHeight: 1,
                  color: '#416ba9',
                }}
              >
                +
              </Button>
            </div>
            {styleConfigs.map((config, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  gap: 8,
                  marginBottom: 8,
                  alignItems: 'flex-start',
                }}
              >
                <Dropdown
                  trigger={['click']}
                  placement="bottomLeft"
                  getPopupContainer={(trigger) => trigger.parentElement || trigger}
                  menu={{
                    items: availableLanguages.map(lang => ({
                      key: lang.code,
                      label: lang.label,
                    })) as MenuProps['items'],
                    selectable: true,
                    selectedKeys: [config.sourceLang],
                    onClick: ({ key }) =>
                      handleStyleConfigChange(index, 'sourceLang', key as string),
                  }}
                >
                  <div
                    style={{
                      width: 90,
                      height: 30,
                      border: '1px solid #d9d9d9',
                      borderRadius: 4,
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      fontSize: 13,
                      color: '#333',
                    }}
                  >
                    <span style={{ flex: 1, whiteSpace: 'nowrap' }}>
                      {allLanguagesRecord[config.sourceLang] || config.sourceLang}
                    </span>
                    <DownOutlined style={{ fontSize: 10, color: '#416ba9', marginLeft: 4 }} />
                  </div>
                </Dropdown>
                <TextArea
                  value={config.text}
                  onChange={(e) =>
                    handleStyleConfigChange(index, 'text', e.target.value)
                  }
                  placeholder='请输入翻译术语映射，如：
"香港"->"Hong Kong";
"小子"->"Kid";'
                  autoSize={{ minRows: 3, maxRows: 3 }}
                  style={{ flex: 1, overflowY: 'auto' }}
                  className="term-textarea"
                />
              </div>
            ))}
          </div>

          {/* AI 配音 */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 600, color: '#416ba9', fontSize: '16px' }}>AI配音</span>
              <Switch
                className="ai-switch"
                style={{ marginLeft: 10 }}
                checked={aiVoiceEnabled}
                onChange={setAiVoiceEnabled}
              />
            </div>
            {/* 积分注释 */}
            {/* <div style={{ marginTop: 8, color: '#999' }}>
              预计扣除 6350 积分
            </div> */}
          </div>
        </Modal>

      </div>
    </>
  );
};